import React, { useState } from 'react'
import c from 'classnames'
import * as Yup from 'yup'
import _pick from 'lodash/pick'
import _mapValues from 'lodash/mapValues'
import { Formik, Field, FormikProps } from 'formik'

import { MoveDatesType } from '@/__gen/types'
import { TextField, ImageField, SelectField, handleCharacterLimitError } from '@/components/common/formik'
import { SpinnerButton } from '@/components/common/button'
import { GlobalContextProps, useGlobalContext } from '@/components/context'
import buttonStyle from '@sass/common/button.module.sass'
import generalStyle from '@sass/common/general.module.sass'
import formStyle from '@sass/common/form.module.sass'
import { MoveDateOnCopyInput } from './move-date-on-copy'
import style from './form.module.sass'
import { UpdateRecordMutationVariables, RecordTemplateOptionsQuery } from './__gen'


type RecordOption = NonNullable<RecordTemplateOptionsQuery["recordOptions"]>["objects"][number]
type TemplateOption = NonNullable<RecordTemplateOptionsQuery["templateOptions"]>["objects"][number]
type TeamOption = GlobalContextProps["currentModule"]["teams"][number]

export type Values = {
	id: string | null
	position: string
	template: TemplateOption | null
	copyRecord?: RecordOption | null
	team: { id: string } | null
	name: string
	image: string | File | null
	moveDatesTo?: number
	moveDatesType?: {value: MoveDatesType, label: string}
}

const NAME_LENGTH_LIMIT = 100
const POSITION_LENGTH_LIMIT = 40

type Props = {
	initialValues: Values
	dedicatedCopyUrl?: boolean
	recordOptions: RecordOption[]
	templateOptions: TemplateOption[]
	onSubmit: (variables: UpdateRecordMutationVariables) => Promise<any>
}

const getValidationSchema = () => {
	const idObj = Yup.object().shape({ id: Yup.string().required() }).nullable()

	return Yup.object().shape({
		id: Yup.string().nullable(),
		name: Yup.string().max(NAME_LENGTH_LIMIT, "Character limit reached"),
		position: Yup.string().max(POSITION_LENGTH_LIMIT, "Character limit reached"),
		image: Yup.mixed().nullable(),
		template: idObj.required('Please select a template'),
		team: idObj.required(`Please select a ${useGlobalContext().currentModule.teamWord}`),
		moveDatesType: Yup.object().shape({ value: Yup.string() }),
		moveDatesTo: Yup.number()
			.when('moveDatesType', {
				is: (v: any) => v && v.value === MoveDatesType.YearDelta,
				then: (schema: Yup.NumberSchema) =>
					schema.min(0, 'Must be greater than 0').max(100, 'Must be less than 100'),
				otherwise: (schema: Yup.NumberSchema) =>
					schema.min(1900, 'Must be greater than 1900').max(2100, 'Must be less than 2100'),
			})
			.nullable()
			.typeError('Error, must be a number'),
	})
}

const EditForm: React.FC<Props> = ({ initialValues, templateOptions, recordOptions, onSubmit, dedicatedCopyUrl}) => {
	const { currentModule } = useGlobalContext()
	const [copyFromRecord, setcopyFromRecord] = useState(!!initialValues.copyRecord)
	const [customisePositionWord, setCustomisePositionWord] = useState(
		initialValues.template && initialValues.id
		&& (initialValues.position != initialValues.template.position)
	)
	return (
		<Formik
			initialValues={initialValues}
			validationSchema={getValidationSchema()}
			onSubmit={(values, actions) => {
				// Clear previous 'all' error for new frontend validation checks
				actions.setFieldError('all', undefined);

				const ids = _pick(values, ['template', 'team', 'copyRecord'])
				const aIds = _mapValues(ids, 'id') as {
					template: string
					team: string
				}
				
				const saveData: UpdateRecordMutationVariables = {
					..._pick(values, ['id', 'position', 'name', 'image', 'moveDatesTo']),
					...aIds,
					moveDatesType: values.moveDatesType ? values.moveDatesType.value : null,
				}
				onSubmit(saveData).then(r => {
					// actions.resetForm()
				}).catch((r: Error) => {
					console.error(r)
					actions.setFieldError('all', 'There was a problem saving data')
				}).finally(() => {
					actions.setSubmitting(false)
				})
			}}
			validateOnChange={true}
			validateOnBlur={true}
		>
			{({ values, errors, isSubmitting, handleSubmit, setFieldValue, setFieldError }: FormikProps<Values>) => {
				const namePlaceholder = (
					(values.template && values.template.namePlaceholder)
					|| (values.copyRecord && values.copyRecord.template.namePlaceholder)
				)
				return (
					<form onSubmit={handleSubmit} className={formStyle.commonForm}>
						{ ! copyFromRecord &&
							<Field
								name="template"
								placeholder={`Select ${currentModule.positionWord.toLowerCase()}...`}
								component={SelectField}
								isDisabled={
									(initialValues.id && !currentModule.allowRecordTemplateChange)
									|| (templateOptions.length == 1 && values.template == templateOptions[0])
								}
								options={templateOptions}
								onChange={(t: TemplateOption) => {
									if (t.copyPositionText) {
										setCustomisePositionWord(false)
										setFieldValue("position", t.position)
									} else {
										setCustomisePositionWord(true)
										setFieldValue("position", '')
									}
								}}
								formatOptionLabel={(r: TemplateOption) => (
									<React.Fragment>
										<b>{r.position}</b>
									</React.Fragment>
								)}
								getOptionValue={(o: TemplateOption) => o.id}
								maxMenuHeight={220}
								filterOption={(o: {data: TemplateOption}, input: string) => o.data.position.includes(input)}
							/>
						}
						
						{ copyFromRecord &&
							<Field
								name="copyRecord"
								placeholder={`Copy ${currentModule.recordWord.toLowerCase()}...`}
								component={SelectField}
								isDisabled={
									(initialValues.id && !currentModule.allowRecordTemplateChange) ||
									(dedicatedCopyUrl && values.template?.id && values.copyRecord?.template.id === values.template.id)
								}
								options={recordOptions}
								onChange={(r: RecordOption) => {
									setFieldValue("position", r.position)
									setFieldValue("template", r.template)
								}}
								formatOptionLabel={(r: RecordOption) => (
									<React.Fragment>
										<b>{r.position}</b>
										{r.position && r.name && ' - '}
										{r.name}
									</React.Fragment>
								)}
								getOptionValue={(r: RecordOption) => r.id}
								maxMenuHeight={220}
								filterOption={(o: {data: RecordOption}, input: string) => (
									o.data.position.toLowerCase().includes(input.toLowerCase())
									|| o.data.name.toLowerCase().includes(input.toLowerCase())
								)}
								/>
						}
						{ recordOptions.length > 0
							&& currentModule.allowRecordTemplateChange
							&& !dedicatedCopyUrl &&
							<button
								className={c(style.editFormSmallButton, buttonStyle.small, buttonStyle.grey, buttonStyle.noOutline)}
								style={{margin: '0'}}
								onClick={() => {
									setcopyFromRecord(!copyFromRecord)
									setFieldValue("template", null)
									setFieldValue("copyRecord", null)
								}}
								type="button"
							>
								{values.id
									? copyFromRecord
										? `Change ${currentModule.positionWord.toLowerCase()}`
										: `Change ${currentModule.positionWord.toLowerCase()}
											to existing ${currentModule.recordWord.toLowerCase()}`
									: copyFromRecord 
										? `Or create from ${currentModule.positionWord.toLowerCase()} template`
										: `Or copy from existing ${currentModule.recordWord.toLowerCase()}`
								}
							</button>
						}

						<Field
							name="name"
							component={TextField}
							placeholder={namePlaceholder || 'Name'}
							maxLength={NAME_LENGTH_LIMIT + 1}
							showErrorWhenTouched={false}
						/>

						{customisePositionWord && (
							<Field
								name="position"
								placeholder={currentModule.positionWord}
								component={TextField}
								maxLength={POSITION_LENGTH_LIMIT + 1}
								showErrorWhenTouched={false}
							/>
						)}

						{ currentModule.allowPositionTextChange &&
							(values.template || values.copyRecord) &&
							<button
								className={c(style.editFormSmallButton, buttonStyle.small, buttonStyle.grey, buttonStyle.noOutline)}
								style={{margin: '0'}}
								onClick={() => {
									setCustomisePositionWord(!customisePositionWord)
									setFieldValue(
										"position",
										(values.template && values.template.position) || (values.copyRecord && values.copyRecord.position),
									)
								}}
								type="button"
							>
								{ customisePositionWord
									? `Use default ${currentModule.positionWord.toLowerCase()} title`
									: `Customise ${currentModule.positionWord.toLowerCase()} title`
								}
							</button>
						}
						
						{ (currentModule.teams.length > 1 || !values.team) &&
							<Field
								placeholder={currentModule.teamWord}
								name="team"
								component={SelectField}
								options={currentModule.teams}
								formatOptionLabel={(t: NonNullable<GlobalContextProps['currentTeam']>) => <span>
									<b>{t.name}</b>
								</span>}
								getOptionValue={(t: NonNullable<GlobalContextProps['currentTeam']>) => t.id}
								maxMenuHeight={160}
								filterOption={(o: {data: TeamOption}, input: string) => o.data.name.toLowerCase().includes(input.toLowerCase())}

							/>
						}

						{values.copyRecord && values.template &&
							<MoveDateOnCopyInput
								templateId={values.template.id}
								updateValue={(moveDatesTo: number) => setFieldValue("moveDatesTo", moveDatesTo)}
								moveDatesTo={values.moveDatesTo}
								moveDatesType={values.moveDatesType}
								setFieldValue={setFieldValue}
							/>
						}

						{ currentModule.recordImages &&
							<Field
								title="Image"
								name="image"
								component={ImageField}
							/>
						}

						{/* 
						//@ts-ignore */}
						<div className={generalStyle.error}>{errors.all}</div>
						<div className={formStyle.formButton}>
							<SpinnerButton loading={isSubmitting} type="submit">
								{values.id ? 'Save' : 'Create'}
							</SpinnerButton>
						</div>
					</form>
				)
			}}
		</Formik>
	)
}
export default EditForm